import React from "react";
import { Link } from "react-router-dom";

// When this text changes, update LAST_UPDATED and TERMS_VERSION in krunchmate-middleware/server.js,
// so each order records which version the customer agreed to.
const LAST_UPDATED = "29 October 2026";
const SUPPORT_EMAIL = "contact@krunchmate.com";

const SupportLink = () => (
  <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[color:var(--gold)] underline">
    {SUPPORT_EMAIL}
  </a>
);

const SECTIONS = [
  {
    id: "about",
    title: "About these terms",
    body: (
      <>
        <p>
          These terms apply to every order placed on krunchmate.com. The seller is KrunchMate Limited, a company
          registered in England and Wales (company number 16977257), of 71-75 Shelton Street, Covent Garden,
          London, WC2H 9JQ. You can reach us at <SupportLink />.
        </p>
        <p>You'll be asked to agree to these terms before you pay. Please read them first.</p>
      </>
    ),
  },
  {
    id: "cancellations",
    title: "Order cancellations & refunds",
    body: (
      <>
        <p>
          Once an order is placed, it cannot be cancelled or refunded if you change your mind, except where the
          law gives you the right to cancel, as set out below.
        </p>
        <p>
          Under the Consumer Contracts Regulations 2013 you may cancel within 14 days of receiving your order.
          This right doesn't cover pouches that have been opened: our snacks are sealed for health and hygiene
          reasons, so they can't be returned once unsealed.
        </p>
        <p>
          To cancel, email <SupportLink /> within those 14 days, then send the unopened pouches back within 14
          days of telling us. Return postage is at your cost. We'll refund the price of the returned items, and
          the standard delivery charge if you return the whole order, within 14 days of receiving them.
        </p>
      </>
    ),
  },
  {
    id: "delivery-guarantee",
    title: "10-day delivery guarantee",
    body: (
      <p>
        If an order is not delivered within a maximum of 10 calendar days from dispatch, you are eligible for a
        full refund alongside a one-time use coupon of up to 50% off your next order. To claim, email{" "}
        <SupportLink /> with your order number.
      </p>
    ),
  },
  {
    id: "damaged-items",
    title: "Damaged items",
    body: (
      <>
        <p>
          If individual pouches or products arrive damaged due to transit, you may request a partial refund for
          the damaged goods or add-on compensation products on your next shipment. If the entire order is damaged
          in transit, you are eligible for a full refund, provided the damage occurred during transit and was not
          caused by adverse weather conditions after delivery.
        </p>
        <p>
          Please email <SupportLink /> with your order number and a photo of the damage. Your order is our
          responsibility until it's delivered, and under the Consumer Rights Act 2015 you can always choose a
          refund for goods that arrive damaged, rather than replacement products, if you tell us within 30 days
          of delivery.
        </p>
      </>
    ),
  },
  {
    id: "delivery-responsibility",
    title: "Delivery & courier responsibility",
    body: (
      <>
        <p>
          You must arrange and ensure a proper, secure location for your order to be delivered. Delivery
          confirmation provided by Royal Mail or the designated courier's tracking details serves as our proof
          of delivery.
        </p>
        <p>
          If tracking shows your order as delivered but you haven't received it, email <SupportLink /> within 7
          days and we'll look into it with the courier.
        </p>
      </>
    ),
  },
  {
    id: "statutory-rights",
    title: "Your legal rights",
    body: (
      <p>
        Nothing in these terms affects your legal rights as a consumer. For free, independent advice, contact
        Citizens Advice.
      </p>
    ),
  },
];

export default function Terms() {
  return (
    <div className="pt-32 pb-28 px-5 lg:px-10" data-testid="terms-page">
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4 lg:sticky lg:top-32">
            <h1 className="font-display text-cream text-5xl lg:text-6xl leading-[0.94]">
              Terms &<br />
              <span className="font-beth text-[color:var(--gold)] normal-case italic">conditions.</span>
            </h1>
            <p className="mt-6 text-cream/75 leading-relaxed">Last updated {LAST_UPDATED}.</p>
            <nav aria-label="Sections" className="mt-8 hidden lg:block">
              <ul className="space-y-2 text-sm">
                {SECTIONS.map((section) => (
                  <li key={section.id}>
                    <a href={`#${section.id}`} className="text-cream/60 hover:text-[color:var(--gold)] transition-colors">
                      {section.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          <div className="lg:col-span-8 divide-y divide-cream/10 border-y border-cream/10">
            {SECTIONS.map((section) => (
              <section key={section.id} id={section.id} className="py-8 scroll-mt-28">
                <h2 className="font-display text-cream text-xl lg:text-2xl leading-tight mb-4">{section.title}</h2>
                <div className="space-y-4 text-cream/75 leading-relaxed">{section.body}</div>
              </section>
            ))}
            <p className="py-8 text-cream/60 text-sm">
              Questions about an order? <Link to="/contact" className="text-[color:var(--gold)] underline">Contact us</Link>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
